import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import { FormFieldState } from '../../../../core/components';

type FieldOf<V extends Record<string, string>> = keyof V & string;

type UseTouchedFormStateOptions<V extends Record<string, string>> = {
  schema: z.ZodType<V>;
  initialValues: V;
  /** Campos que devem sempre mostrar `'default'`, ignorando o valor/erro (ex.: um campo condicional a outra escolha do form). */
  isFieldSkipped?: (field: FieldOf<V>, values: V) => boolean;
};

type UseTouchedFormStateResult<V extends Record<string, string>> = {
  values: V;
  setValues: Dispatch<SetStateAction<V>>;
  isValid: boolean;
  handleBlur: (field: FieldOf<V>) => void;
  getFieldState: (field: FieldOf<V>) => FormFieldState;
  getFieldError: (field: FieldOf<V>) => string | undefined;
};

/**
 * Estado comum a todo formulário do fluxo de suporte: valores, "touched" por campo e validação via
 * `zod`, com erro só exibido após o blur do campo (ver `useChannelForm`/`usePersonalDataForm`/`useDescriptionForm`).
 */
export function useTouchedFormState<V extends Record<string, string>>({
  schema,
  initialValues,
  isFieldSkipped,
}: UseTouchedFormStateOptions<V>): UseTouchedFormStateResult<V> {
  const [values, setValues] = useState<V>(initialValues);
  const [touched, setTouched] = useState<Partial<Record<FieldOf<V>, boolean>>>({});

  const parseResult = useMemo(() => schema.safeParse(values), [schema, values]);

  const handleBlur = useCallback((field: FieldOf<V>) => {
    setTouched((previous) => ({ ...previous, [field]: true }));
  }, []);

  const getFieldError = useCallback(
    (field: FieldOf<V>): string | undefined => {
      if (!touched[field] || parseResult.success) {
        return undefined;
      }
      return parseResult.error.issues.find((issue) => issue.path[0] === field)?.message;
    },
    [touched, parseResult],
  );

  const getFieldState = useCallback(
    (field: FieldOf<V>): FormFieldState => {
      if (isFieldSkipped?.(field, values)) {
        return 'default';
      }

      const hasError =
        !parseResult.success && parseResult.error.issues.some((issue) => issue.path[0] === field);
      const isEmpty = values[field].trim() === '';

      if (!isEmpty && !hasError) {
        return 'valid';
      }
      return touched[field] && hasError ? 'error' : 'default';
    },
    [values, touched, parseResult, isFieldSkipped],
  );

  return {
    values,
    setValues,
    isValid: parseResult.success,
    handleBlur,
    getFieldState,
    getFieldError,
  };
}
